import axios from 'axios';
import { isInChinaSync } from '../utils/regionDetect';

const clientApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
});

// Request interceptor - attach client_token + region hint
clientApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('client_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Mirrors the staff axios interceptor: lets AI endpoints pick the
    // right provider per user (Kimi for CN, OpenAI elsewhere).
    if (isInChinaSync()) {
      config.headers['X-User-Region'] = 'cn';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - clear client_token on 401
clientApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isAuthEndpoint = error.config?.url?.includes('/client/auth/');
      if (!isAuthEndpoint) {
        localStorage.removeItem('client_token');
        delete clientApi.defaults.headers.common['Authorization'];
      }
    }
    return Promise.reject(error);
  }
);

// Client Auth API
export const clientAuthAPI = {
  login: (data) => clientApi.post('/client/auth/login', data),
  register: (data) => clientApi.post('/client/auth/register', data),
  getMe: () => clientApi.get('/client/auth/me'),
  updatePassword: (data) => clientApi.put('/client/auth/password', data),
  sendOTP: (data) => clientApi.post('/client/auth/send-otp', data),
  verifyOTP: (data) => clientApi.post('/client/auth/verify-otp', data),
  forgotPassword: (data) => clientApi.post('/client/auth/forgot-password', data),
  resetPassword: (data) => clientApi.post('/client/auth/reset-password', data),
  getSocialLoginSettings: () => clientApi.get('/client/auth/social-login-settings'),
  googleLogin: (data) => clientApi.post('/client/auth/google-login', data),
  appleLogin: (data) => clientApi.post('/client/auth/apple-login', data),
};

// Client Translate API — server-side LibreTranslate proxy (China-safe).
export const clientTranslateAPI = {
  translate: (text, from, to) => clientApi.post('/client/translate', { text, from, to }),
  batch: (texts, from, to) => clientApi.post('/client/translate/batch', { texts, from, to }),
};

// Client Profiles API
export const clientProfilesAPI = {
  browse: (params) => clientApi.get('/client/profiles', { params }),
  getProfile: (id) => clientApi.get(`/client/profiles/${id}`),
  getLocations: (params) => clientApi.get('/client/profiles/locations', { params }),
  getFilterableFields: () => clientApi.get('/client/profiles/filterable-fields'),
  updateProfile: (data) => clientApi.put('/client/profile', data),
  uploadPhoto: (formData) => clientApi.post('/client/profile/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Client Messages API
export const clientMessagesAPI = {
  getConversations: () => clientApi.get('/client/messages/conversations'),
  getMessages: (conversationId) => clientApi.get(`/client/messages/${conversationId}`),
  sendMessage: (data) => clientApi.post('/client/messages/send', data),
  getUnreadCount: () => clientApi.get('/client/messages/unread'),
};

// Client Trends API
export const clientTrendsAPI = {
  getCategories: () => clientApi.get('/client/trends/categories'),
  getTrends: (params) => clientApi.get('/client/trends', { params }),
  getTrend: (id) => clientApi.get(`/client/trends/${id}`),
  getComments: (id) => clientApi.get(`/client/trends/${id}/comments`),
  getRelated: (id) => clientApi.get(`/client/trends/${id}/related`),
  likeTrend: (id) => clientApi.post(`/client/trends/${id}/like`),
  commentOnTrend: (id, data) => clientApi.post(`/client/trends/${id}/comment`, data),
  votePoll: (id, data) => clientApi.post(`/client/trends/${id}/vote`, data),
  shareTrend: (id) => clientApi.post(`/client/trends/${id}/share`),
  // Client-authored trends
  createTrend: (data) => clientApi.post('/client/trends', data),
  updateTrend: (id, data) => clientApi.put(`/client/trends/${id}`, data),
  deleteTrend: (id) => clientApi.delete(`/client/trends/${id}`),
  getMyTrends: (params) => clientApi.get('/client/my-trends', { params }),
};

// Client Gallery API
export const clientGalleryAPI = {
  getGallery: () => clientApi.get('/client/me/gallery'),
  uploadPhotos: (formData) => clientApi.post('/client/me/gallery', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deletePhoto: (photo) => clientApi.delete('/client/me/gallery', { data: { photo } }),
  setCover: (photo) => clientApi.put('/client/me/gallery/cover', { photo }),
};

// Client My Trends API
export const clientMyTrendsAPI = {
  getMyTrends: (params) => clientApi.get('/client/me/trends', { params }),
};

// Client Visitors API
export const clientVisitorsAPI = {
  getVisitors: () => clientApi.get('/client/me/visitors'),
};

// Client Following API
export const clientFollowingAPI = {
  getFollowingData: () => clientApi.get('/client/me/following'),
};

// Client Matches API
export const clientMatchesAPI = {
  getMatches: (params) => clientApi.get('/client/matches', { params }),
};

// Client Dating Schedules API
export const clientDatingScheduleAPI = {
  getSchedules: (params) => clientApi.get('/client/dating-schedules', { params }),
};

// Client Notifications API
export const clientNotificationsAPI = {
  getNotifications: (params) => clientApi.get('/client/notifications', { params }),
  markRead: (id) => clientApi.put(`/client/notifications/${id}/read`),
};

// Client AI API
export const clientAIAPI = {
  chat: (data) => clientApi.post('/client/ai/chat', data),
  getPreferences: () => clientApi.get('/client/ai/preferences'),
  savePreferences: (data) => clientApi.put('/client/ai/preferences', data),
};

// Client Favorites API
export const clientFavoritesAPI = {
  getFavorites: () => clientApi.get('/client/favorites'),
  toggleFavorite: (profileId) => clientApi.post(`/client/favorites/${profileId}`),
};

// Client Experiences API — 活动与成长项目 (events & programmes), read-only in phase 1.
//   list({ type: 'event'|'program', city, from, price: 'free'|'paid', q, page, limit })
//   get(idOrSlug)
export const clientExperiencesAPI = {
  list: (params) => clientApi.get('/client/experiences', { params }),
  get: (idOrSlug) => clientApi.get(`/client/experiences/${encodeURIComponent(idOrSlug)}`),
  // Phase 2 — ticketing. checkout(id, { items: [{ ticket_type_id, quantity }], attendee_names: [] })
  //   → free: { order, tickets, paid: true }; paid: { order, payment: { provider, application_id, location_id, environment, amount_cents } }
  checkout: (id, data) => clientApi.post(`/client/experiences/${encodeURIComponent(id)}/checkout`, data),
};

// Client Orders & Tickets — 活动票务 (phase 2).
//   pay(orderId, { source_id, verification_token? }) → { order, tickets } | 402 { code: 'CARD_DECLINED' }
//   refund(orderId) → 403 { code: 'REFUND_WINDOW_CLOSED' } when inside the no-self-refund window
export const clientOrdersAPI = {
  pay: (orderId, data) => clientApi.post(`/client/orders/${orderId}/pay`, data),
  cancel: (orderId) => clientApi.post(`/client/orders/${orderId}/cancel`),
  refund: (orderId) => clientApi.post(`/client/orders/${orderId}/refund`),
  myTickets: (scope) => clientApi.get('/client/me/tickets', { params: { scope } }),
  myOrders: () => clientApi.get('/client/me/orders'),
};

// 月度服务报告 — published reports only.
//   list() → [{ id, period_index, period_start, period_end, published_at }] (or { reports: [...] })
//   get(id) → { id, period_index, period_start, period_end, published_at, opening, stats, timeline, learned, plan, author, reviewer }
export const clientServiceReportsAPI = {
  list: () => clientApi.get('/client/me/service-reports'),
  get: (id) => clientApi.get(`/client/me/service-reports/${id}`),
};

// 恋爱课堂 (love-skills course).
//   list() → { member, bundle: { price, available }, progress, continue_lesson, units: [{ id, title, subtitle, access, price,
//              unlocked, can_buy_unit, can_buy_bundle, lessons: [{ id, title, minutes, audience, is_trial, locked, completed, bookmarked }] }] }
//   lesson(id) → { locked: false, lesson: { blocks… }, unit, index, prev, next, completed, exercise_answer, bookmarked }
//              | 402 { locked: true, lesson, summary, goals, unit: { title, price, access, lessons }, bundle }
//   checkout({ product: 'unit'|'bundle_basic', unit_id? }) → { purchase, payment: { application_id, location_id, environment, amount_cents }, title }
//   pay(purchaseId, { source_id, verification_token? }) → { purchase, unlocked } | 402 { code: 'CARD_DECLINED' }
export const clientCoursesAPI = {
  list: () => clientApi.get('/client/courses'),
  lesson: (id) => clientApi.get(`/client/courses/lessons/${id}`),
  complete: (id, data) => clientApi.post(`/client/courses/lessons/${id}/complete`, data || {}),
  bookmark: (id) => clientApi.put(`/client/courses/lessons/${id}/bookmark`),
  unbookmark: (id) => clientApi.delete(`/client/courses/lessons/${id}/bookmark`),
  checkout: (data) => clientApi.post('/client/courses/checkout', data),
  pay: (purchaseId, data) => clientApi.post(`/client/courses/purchases/${purchaseId}/pay`, data),
  purchases: () => clientApi.get('/client/courses/purchases'),
};

// Public (no-auth) experience share page — /e/:slug. Plain axios on purpose:
// no member token, and a 401 here must not clear the member's session.
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: { 'Content-Type': 'application/json' },
});
export const publicExperiencesAPI = {
  get: (slug) => publicApi.get(`/public/experiences/${encodeURIComponent(slug)}`),
};

// 会员评价 — rate my 售后红娘. get → { coach, rating, last_rating, window_days, tags, max_comment };
// save({ stars, tags, comment }) upserts the rating of the current 30-day window.
export const clientCoachRatingAPI = {
  get: () => clientApi.get('/client/me/coach-rating'),
  save: (data) => clientApi.put('/client/me/coach-rating', data),
};

export default clientApi;
